import { useMemo } from 'react';
import { components } from '@lifeserver/models/events';
import {
    skipToken,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import axios from 'axios';

// Types (generated from the Events service OpenAPI spec)
export type EventItem = components['schemas']['EventDTO'];
export type ActivationItem = components['schemas']['ActivationDTO'];
export type ScheduleItem = components['schemas']['ActivationPublicationDTO'];
export type EventSchedule = components['schemas']['EventScheduleDTO'];
type CreateEventDTO = components['schemas']['CreateEventDTO'];
type UpdateEventVersionDTO = components['schemas']['UpdateEventVersionDTO'];
type CreateActivationDTO = components['schemas']['CreateActivationDTO'];
type PutScheduleDTO = components['schemas']['PutScheduleDTO'];

// URL constants
const EVENTS_BASE = '/api/events/admin';
export const EVENTS_URL = `${EVENTS_BASE}/events`;
export const ACTIVATIONS_URL = `${EVENTS_BASE}/activations`;
export const SCHEDULE_URL = `${EVENTS_BASE}/schedule`;

// API class
class EventsApi {
    fetchEvents = async (): Promise<EventItem[]> => {
        const { data } = await axios.get<EventItem[]>(EVENTS_URL);
        return data;
    };

    createEvent = async (dto: CreateEventDTO): Promise<EventItem> => {
        const { data } = await axios.post<EventItem>(EVENTS_URL, dto);
        return data;
    };

    deleteEvent = async (id: string): Promise<void> => {
        await axios.delete(`${EVENTS_URL}/${id}`);
    };

    updateEventVersion = async ({
        id,
        dto,
    }: {
        id: string;
        dto: UpdateEventVersionDTO;
    }): Promise<EventItem> => {
        const { data } = await axios.put<EventItem>(
            `${EVENTS_URL}/${id}/version`,
            dto,
        );
        return data;
    };

    fetchActivations = async (): Promise<ActivationItem[]> => {
        const { data } = await axios.get<ActivationItem[]>(ACTIVATIONS_URL);
        return data;
    };

    createActivation = async (
        dto: CreateActivationDTO,
    ): Promise<ActivationItem> => {
        const { data } = await axios.post<ActivationItem>(ACTIVATIONS_URL, dto);
        return data;
    };

    deleteActivation = async (id: string): Promise<void> => {
        await axios.delete(`${ACTIVATIONS_URL}/${id}`);
    };

    fetchSchedule = async (eventId: string): Promise<EventSchedule> => {
        const { data } = await axios.get<EventSchedule>(
            `${SCHEDULE_URL}/${eventId}`,
        );
        return data;
    };

    saveSchedule = async ({
        eventId,
        body,
    }: {
        eventId: string;
        body: PutScheduleDTO;
    }): Promise<EventSchedule> => {
        const { data } = await axios.put<EventSchedule>(
            `${SCHEDULE_URL}/${eventId}`,
            body,
        );
        return data;
    };
}

// API singleton hook
export const useEventsApi = () => {
    return useMemo(() => new EventsApi(), []);
};

// Query hooks
export const useEvents = () => {
    const api = useEventsApi();
    const client = useQueryClient();

    const query = useQuery({
        queryKey: [EVENTS_URL],
        queryFn: api.fetchEvents,
    });

    const createEvent = useMutation({
        mutationFn: api.createEvent,
        onSettled: () => {
            client.invalidateQueries({ queryKey: [EVENTS_URL] });
        },
    });

    const deleteEvent = useMutation({
        mutationFn: api.deleteEvent,
        onSettled: () => {
            client.invalidateQueries({ queryKey: [EVENTS_URL] });
            client.invalidateQueries({ queryKey: [ACTIVATIONS_URL] });
            client.invalidateQueries({ queryKey: [SCHEDULE_URL] });
        },
    });

    const updateEventVersion = useMutation({
        mutationFn: api.updateEventVersion,
        onSettled: () => {
            client.invalidateQueries({ queryKey: [EVENTS_URL] });
        },
    });

    return { ...query, createEvent, deleteEvent, updateEventVersion };
};

export const useActivations = () => {
    const api = useEventsApi();
    const client = useQueryClient();

    const query = useQuery({
        queryKey: [ACTIVATIONS_URL],
        queryFn: api.fetchActivations,
    });

    const createActivation = useMutation({
        mutationFn: api.createActivation,
        onSettled: () => {
            client.invalidateQueries({ queryKey: [ACTIVATIONS_URL] });
        },
    });

    const deleteActivation = useMutation({
        mutationFn: api.deleteActivation,
        onSettled: () => {
            client.invalidateQueries({ queryKey: [ACTIVATIONS_URL] });
            client.invalidateQueries({ queryKey: [SCHEDULE_URL] });
        },
    });

    return { ...query, createActivation, deleteActivation };
};

export const useEventSchedule = (eventId?: string) => {
    const api = useEventsApi();
    const client = useQueryClient();

    const query = useQuery({
        queryKey: [SCHEDULE_URL, eventId],
        queryFn: eventId ? () => api.fetchSchedule(eventId) : skipToken,
    });

    const saveSchedule = useMutation({
        mutationFn: (body: PutScheduleDTO) => {
            if (!eventId) throw new Error('No eventId selected');
            return api.saveSchedule({ eventId, body });
        },
        onSettled: () => {
            client.invalidateQueries({ queryKey: [SCHEDULE_URL, eventId] });
        },
    });

    return { ...query, saveSchedule };
};
